import { Injectable } from '@jkhy/frontend-utils/di';
import {
    BannerAndModalMessageConfigurationClient,
    BannerAndModalMessageConfigurationModelDto,
    InformationCenterConfigurationModelDto,
    InformationCenterConfigurationsClient,
    InstitutionProductsClient,
    ResourceCenterConfigurationsClient,
    ResourceConfigurationModelDto,
} from '@treasury/api/bo';
import { BannerMessage } from './entities/banner-message.entity';
import { InformationCenterMessage } from './entities/information-center-message.entity';
import { InstitutionProduct } from './entities/institution-product.entity';
import { ResourceLink } from './entities/resource-link.entity';

@Injectable()
export class DashboardConfigurationService {
    constructor(
        private readonly bannerClient: BannerAndModalMessageConfigurationClient,
        private readonly infoCenterClient: InformationCenterConfigurationsClient,
        private readonly resourceClient: ResourceCenterConfigurationsClient,
        private readonly institutionProductsClient: InstitutionProductsClient
    ) {}

    // --- Banner & Modal Messages ---

    public async getBannerMessages(): Promise<BannerMessage[]> {
        const response =
            await this.bannerClient.informationCenterConfigurationsGetBannerAndModalMessageConfiguration();
        return (response.data ?? []).map(dto => BannerMessage.from(dto));
    }

    public async createBannerMessage(message: BannerMessage): Promise<BannerMessage> {
        const response = await this.bannerClient.informationCenterConfigurationsPost2(
            message.toDto() as BannerAndModalMessageConfigurationModelDto
        );
        return BannerMessage.from(response.data);
    }

    public async updateBannerMessage(message: BannerMessage): Promise<BannerMessage> {
        const response = await this.bannerClient.informationCenterConfigurationsPut2(
            message.id,
            message.toDto() as BannerAndModalMessageConfigurationModelDto
        );
        return BannerMessage.from(response.data);
    }

    public async deleteBannerMessage(id: number): Promise<void> {
        await this.bannerClient.informationCenterConfigurationsDeleteBannerAndModalMessageConfiguration(
            id
        );
    }

    public async changeBannerMessageStatus(id: number, status: string): Promise<void> {
        await this.bannerClient.informationCenterConfigurationsChangeStatus(id, status);
    }

    // --- Information Center Messages ---

    public async getInformationCenterMessages(): Promise<InformationCenterMessage[]> {
        const response = await this.infoCenterClient.informationCenterConfigurationsGetAll();
        return (response.data ?? []).map(dto => InformationCenterMessage.from(dto));
    }

    public async createInformationCenterMessage(
        message: InformationCenterMessage
    ): Promise<InformationCenterMessage> {
        const response = await this.infoCenterClient.informationCenterConfigurationsPost(
            message.toDto() as InformationCenterConfigurationModelDto
        );
        return InformationCenterMessage.from(response.data);
    }

    public async updateInformationCenterMessage(
        message: InformationCenterMessage
    ): Promise<InformationCenterMessage> {
        const response = await this.infoCenterClient.informationCenterConfigurationsPut(
            message.id,
            message.toDto() as InformationCenterConfigurationModelDto
        );
        return InformationCenterMessage.from(response.data);
    }

    public async deleteInformationCenterMessage(id: number): Promise<void> {
        await this.infoCenterClient.informationCenterConfigurationsDelete(id);
    }

    public async resequenceInformationCenterMessage(
        id: number,
        direction: 'up' | 'down'
    ): Promise<InformationCenterMessage[]> {
        const response = await this.infoCenterClient.informationCenterConfigurationsResequence({
            id,
            direction,
        });
        return (response.data ?? []).map(dto => InformationCenterMessage.from(dto));
    }

    // --- Resource Links ---

    public async getResourceLinks(): Promise<ResourceLink[]> {
        const response = await this.resourceClient.resourceCenterConfigurationsGetAll();
        return (response.data ?? []).map(dto => ResourceLink.from(dto));
    }

    public async createResourceLink(link: ResourceLink): Promise<ResourceLink> {
        const response = await this.resourceClient.resourceCenterConfigurationsPost(
            link.toDto() as ResourceConfigurationModelDto
        );
        return ResourceLink.from(response.data);
    }

    public async updateResourceLink(link: ResourceLink): Promise<ResourceLink> {
        const response = await this.resourceClient.resourceCenterConfigurationsPut(
            link.id,
            link.toDto() as ResourceConfigurationModelDto
        );
        return ResourceLink.from(response.data);
    }

    public async deleteResourceLink(id: number): Promise<void> {
        await this.resourceClient.resourceCenterConfigurationsDelete(id);
    }

    public async resequenceResourceLink(
        id: number,
        direction: 'up' | 'down'
    ): Promise<ResourceLink[]> {
        const response = await this.resourceClient.resourceCenterConfigurationsResequence({
            id,
            direction,
        });
        return (response.data ?? []).map(dto => ResourceLink.from(dto));
    }

    // --- Institution Products (for Banner/Modal product dropdown) ---

    public async getInstitutionProducts(): Promise<InstitutionProduct[]> {
        const response =
            await this.institutionProductsClient.productFeatureConfigurationsGetInstitutionProducts();
        return (response.data ?? []).map(dto => InstitutionProduct.from(dto));
    }
}
